/**
 * HyperDX browser instrumentation.
 *
 * Gated on HYPERDX_API_KEY the same way mixpanel is gated on MIXPANEL_KEY:
 * absent key, no instrumentation. Cypress and jest runs are excluded so test
 * traffic never reaches the collector.
 *
 * tracePropagationTargets is the part that matters most: it makes the browser
 * send its trace context on API calls, so a request that starts with a click
 * and ends in a stored procedure is one trace instead of two unrelated ones.
 * The target must match the API origin, or the header is dropped silently
 * and the traces stay disconnected.
 */

const enabled =
  typeof window !== 'undefined' &&
  !window.Cypress &&
  !process.env.IS_TEST &&
  process.env.HYPERDX_API_KEY

let HyperDX

if (enabled) {
  // eslint-disable-next-line global-require
  // The package exports init() on the module itself and has NO default
  // export, so `.default` was undefined and HyperDX.init() threw at module
  // load -- taking the whole bundle down with a blank screen. Kept tolerant
  // of both shapes in case a future version adds one.
  const mod = require('@hyperdx/browser')
  HyperDX = mod.default || mod

  HyperDX.init({
    apiKey: process.env.HYPERDX_API_KEY,
    service: process.env.HYPERDX_SERVICE_NAME || 'djc-web',
    url: process.env.HYPERDX_COLLECTOR_URL,

    // Propagate trace context to our own APIs only. A bare string is matched
    // as a substring, so these also cover the per-environment hostnames.
    tracePropagationTargets: [/djcity\.com/i, /localhost/i],

    // Bodies are captured on the API side instead: the server span already
    // carries http.request.body and http.response.body for the same call, so
    // capturing them again here would duplicate every payload.
    advancedNetworkCapture: false,

    consoleCapture: true,

    // Replay starts disabled for everyone and is switched on per session
    // once a user is known to be logged in -- see identifyUser below. An
    // anonymous visitor is only traced, never recorded.
    disableReplay: true,

    // Inputs are masked even when replay is running: the sign-up, login and
    // payment forms would otherwise be recorded keystroke by keystroke.
    maskAllInputs: true,
  })
}

/**
 * Associates the session with a user and starts session replay.
 *
 * Called only from behind the trackingAllowed consent gate: a user who has
 * opted out is still traced, but anonymously and without a recording.
 *
 * Replay is deliberately not enabled for anonymous traffic. It is the
 * heaviest signal we collect -- it ships DOM mutations rather than spans --
 * and the sessions worth watching are the ones belonging to an account we
 * can follow through to the API.
 */
export function identifyUser(user) {
  if (!enabled || !user || !user.CustomerID) {
    return
  }

  const customerId = String(user.CustomerID)

  // The sessions list is labelled from userEmail/userName/teamName only --
  // userId is searchable but never displayed, which is why a session with a
  // valid userId still showed as "Anonymous". Putting the customer id in
  // userName is what makes it appear in that list.
  //
  // Email and name are spread conditionally rather than defaulted to ''.
  // RECEIVE_CURRENT_USER (a returning session) passes CustomerID alone, so
  // writing '' there would overwrite the real values set at login and send
  // the session back to Anonymous the next day.
  HyperDX.setGlobalAttributes({
    userId: customerId,
    userName: customerId,
    // Matches the attribute name the API's spans use, so a browser session
    // and its server spans join on one field.
    customerId,
    ...(user.Email ? { userEmail: user.Email } : {}),
  })

  // init() set disableReplay: true, so nothing has been recorded up to this
  // point in the session. Recording starts here and covers what the user
  // does from now on.
  HyperDX.resumeSessionRecorder()
}

/**
 * Stops recording and clears the identity -- on sign-out, so a shared browser
 * does not keep attributing the next session to the previous account.
 */
export function clearUser() {
  if (!enabled) {
    return
  }

  HyperDX.stopSessionRecorder()
  // Must clear every key identifyUser sets, customerId included, or a shared
  // browser keeps labelling the next session with the previous account.
  HyperDX.setGlobalAttributes({
    userId: '',
    userName: '',
    customerId: '',
    userEmail: '',
  })
}

/** Records a handled exception that would otherwise never reach the collector. */
export function recordException(error, attributes) {
  if (!enabled) {
    return
  }

  HyperDX.recordException(error, attributes)
}

/** Marks a point of interest in the session timeline. */
export function addAction(name, attributes) {
  if (!enabled) {
    return
  }

  HyperDX.addAction(name, attributes)
}

/**
 * onError handler for react-error-boundary, reporting render errors.
 *
 * Deliberately not HyperDX.attachToReactErrorBoundary: that helper overwrites
 * ErrorBoundary.prototype.componentDidCatch and then calls the original
 * unconditionally, so on any version that does not define one it throws
 * inside the error path -- turning a caught render error into an uncaught
 * one. It also patches the shared class, so it cannot be scoped to a single
 * boundary. react-error-boundary v5 exposes onError for exactly this, and its
 * componentDidCatch does nothing else, so no signal is lost by using it.
 *
 * componentStack is the reason this is worth reporting at all: the raw error
 * is usually also caught by the global handler, but only the boundary knows
 * which subtree was rendering when it threw.
 */
export function reportBoundaryError(error, info) {
  if (!enabled) {
    return
  }

  HyperDX.recordException(error, {
    componentStack: info && info.componentStack ? info.componentStack : '',
    // Distinguishes a render error from one recorded by the global handler,
    // which reaches the collector through the same recordException path.
    source: 'react-error-boundary',
  })
}

export const observabilityEnabled = Boolean(enabled)
